import { FeatureCollection, Geometry, Position } from "geojson";
import {
  CustomGeoSource,
  CustomMouseEvent,
  defaultStrokeColor,
  getBooleanColor,
  mapActive,
  mapGradient,
  mapGradientEnd,
  mapGradientStart,
  mapGradientStep,
  notCodedColor,
} from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { GeoJsonNodeProps } from "../../cra/models/CustomGeoJsonProperties";
import { CraNode } from "../../cra/models/CraNode";
import { CraWay } from "../../cra/models/CraWay";
import { HlaWay } from "../../hla/models/HlaWay";
import { RootStore } from "../../shared/stores/RootStore";
import { LifeCycleStage } from "../../cra/models/LifeCycleStage";
import turf from "../../shared/utils/turf";
import { MapMode } from "../../survey/stores/MapStore";
import { MapColoringTreatment, MapColoringType } from "../models/MapColoring";
import { CraAttributes, CraAttributeType } from "@keita/lanepatrol-sdk";
import { getAttributeValue } from "../../coding/utils/attribute-value";

export class CraMapSource {
  map: mapboxgl.Map;
  active = false;
  rootStore: RootStore;
  locationChangeListenerRemovers: (() => void)[] = [];
  waySelectionListenerRemovers: (() => void)[] = [];
  nodeSelectionListenerRemovers: (() => void)[] = [];

  hoverInteractions = {
    point: pointStrokeLayerName,
    simplified: simplifiedStrokeLayerName,
    line: strokeLayerName,
  };

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(simplifiedSourceName, simplifiedSourceConfig);
      this.map.addLayer(simplifiedStrokeLayerConfig);
      this.map.addLayer(simplifiedPointLayerConfig);
      this.map.addLayer(simplifiedLineLayerConfig);
      this.map.addLayer(simplifiedDirectionLayerConfig);

      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(strokeLayerConfig);
      this.map.addLayer(pointMultiSelectedLayerConfig);
      this.map.addLayer(pointStrokeLayerConfig);
      this.map.addLayer(pointInvisibleLayerConfig);
      this.map.addLayer(lineMultiSelectedLayerConfig);
      this.map.addLayer(lineLayerConfig);
      this.map.addLayer(pointLayerConfig);
      this.map.addLayer(directionLayerConfig);
      this.map.addLayer(commentLayerConfig);
    }
  }

  clear() {
    if (mapActive(this.map)) {
      try {
        this.disableLocationChange();
        this.disableNodeSelection();
        this.disableWaySelection();

        this.map.removeLayer(simplifiedStrokeLayerConfig.id);
        this.map.removeLayer(simplifiedPointLayerConfig.id);
        this.map.removeLayer(simplifiedLineLayerConfig.id);
        this.map.removeLayer(simplifiedDirectionLayerConfig.id);
        this.map.removeSource(simplifiedSourceName);

        this.map.removeLayer(strokeLayerConfig.id);
        this.map.removeLayer(pointMultiSelectedLayerConfig.id);
        this.map.removeLayer(pointStrokeLayerConfig.id);
        this.map.removeLayer(pointInvisibleLayerConfig.id);
        this.map.removeLayer(lineMultiSelectedLayerConfig.id);
        this.map.removeLayer(lineLayerConfig.id);
        this.map.removeLayer(pointLayerConfig.id);
        this.map.removeLayer(directionLayerConfig.id);
        this.map.removeLayer(commentLayerConfig.id);
        this.map.removeSource(sourceName);
      } catch (e) {
        console.warn("Error while removing layers and sources", e);
      }
    }
  }

  onModeChange(mode: MapMode) {
    if (mode === MapMode.HomePage) {
      this.setMapData(sourceName, []);
      this.setMapData(simplifiedSourceName, []);
      this.active = false;
    } else {
      this.updateMapData();
      this.active = true;
    }

    this.disableLocationChange();
    this.disableNodeSelection();
    this.disableWaySelection();
    this.disableCursorOnHover();

    if (mode === MapMode.SurveyPage) {
      this.configCursorOnHover();
      this.enableWaySelection();
      this.enableNodeSelection();
    } else if (mode === MapMode.ManageNodes) {
      this.configCursorOnHover();
      this.enableNodeSelection();
      this.enableLocationChanges();
    } else if (mode === MapMode.CraCoding) {
      this.configCursorOnHover();
      this.enableWaySelection();
      this.enableNodeSelection();
    }
  }

  onStoreUpdate() {
    if (!this.active) return;

    this.updateMapData();
  }

  disableLocationChange() {
    this.locationChangeListenerRemovers.map((fn) => fn());
    this.locationChangeListenerRemovers = [];
  }

  private updateMapData() {
    const { filterStore, craNodeStore, craWayStore, craCommentStore } =
      this.rootStore;

    const selectedCraWay = craWayStore.getSelected();
    const selectedNode = craNodeStore.getSelected();

    const geoJsonFeatures: GeoJSON.Feature[] = [];

    const isFeatureVisible = (node: CraNode) => {
      return (
        filterStore.isNodeVisibleByAttributes(node.craWayId, node.id) &&
        filterStore.isNodeVisibleByScore(node.score) &&
        filterStore.isNodeVisibleByTreatment(node.treatments ?? null)
      );
    };

    const getColor = (node: CraNode) => {
      return isFeatureVisible(node)
        ? this.applyMapColoringSettings(node)
        : defaultStrokeColor;
    };

    const getShowCommentIcon = (feature: GeoJSON.Feature): boolean => {
      const props = feature.properties as GeoJsonNodeProps;
      if (
        selectedCraWay?.lifeCycleStage === LifeCycleStage.Coding ||
        selectedCraWay?.lifeCycleStage === LifeCycleStage.Review
      ) {
        const craWay = craWayStore.get(props.craWayId);
        return !!(
          craWay?.stats?.nActiveComments &&
          craCommentStore.commentsByCraWayId
            .get(props.craWayId!)
            ?.find((c) => c.craNodeId === props.id && !c.resolved)
        );
      }
      return false;
    };

    const modifyFeature = (feature: GeoJSON.Feature): GeoJSON.Feature => {
      const props = feature.properties as GeoJsonNodeProps;
      const node = craNodeStore.get(props.id);
      if (!node) return feature;

      return {
        ...feature,
        geometry: applyPendingCoords(props.id, feature.geometry),
        properties: applyFeatureProperties(feature, node),
      };
    };

    const applyPendingCoords = (
      nodeId: string,
      geometry: GeoJSON.Geometry,
    ): GeoJSON.Geometry => {
      if (
        geometry.type === "Point" &&
        craNodeStore.pendingCoordsByNodeId.size
      ) {
        const pendingCoords = craNodeStore.pendingCoordsByNodeId.get(nodeId);

        if (pendingCoords) {
          return {
            ...geometry,
            coordinates: pendingCoords,
          };
        }
      }

      return geometry;
    };

    const applyFeatureProperties = (
      feature: GeoJSON.Feature,
      node: CraNode,
    ): GeoJsonNodeProps => {
      const props = feature.properties as GeoJsonNodeProps;
      const craWaySelected = selectedCraWay?.id === node.craWayId;
      return {
        ...props,
        nodeSelected: selectedNode?.id === node.id,
        nodeMultiSelected: craNodeStore.multiSelectedIds.has(node.id),
        photoMissing: !node.photo,
        color: getColor(node),
        craWaySelected,
        transparent: !!selectedCraWay && !craWaySelected,
        showCommentIcon: getShowCommentIcon(feature),
      };
    };

    filterStore.filteredCraWays.forEach((craWay) => {
      if (filterStore.isCraWayVisible(craWay)) {
        if (craWay?.geoJson) {
          geoJsonFeatures.push(...craWay.geoJson.features.map(modifyFeature));
        }
      }
    });

    this.setMapData(sourceName, geoJsonFeatures);

    const simplifiedFeatures = buildSimplifiedGeoJson(geoJsonFeatures);

    this.setMapData(simplifiedSourceName, simplifiedFeatures);
  }

  private setMapData(sourceName: string, features: GeoJSON.Feature[]) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }

  enableLocationChanges() {
    const { craNodeStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const cursorMove = () => (canvas.style.cursor = "move");
    const cursorDefault = () => (canvas.style.cursor = "");

    this.map.on("mouseenter", pointStrokeLayerName, cursorMove);
    this.map.on("mouseleave", pointStrokeLayerName, cursorDefault);

    const mouseDrag = (e: CustomMouseEvent) => {
      e.preventDefault();
      const feature = e.features?.[0]!;
      canvas.style.cursor = "grab";

      // updates the map features without changing the app state
      const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
        canvas.style.cursor = "grabbing";
        const coords = event.lngLat;
        const source = this.map.getSource(sourceName) as CustomGeoSource;

        // update the coords of the point that was dragged
        source._data.features.forEach((feat, index) => {
          if (feat.properties?.id === feature.id) {
            if (feat.geometry.type === "Point") {
              // needs to be a new instance, to prevent mutation of the original state
              feat.geometry = {
                type: "Point",
                coordinates: [coords.lng, coords.lat],
              };
            }
          }
        });

        source.setData(source._data);
      };

      this.map.on("mousemove", onMouseMove);

      this.map.once("mouseup", (event: mapboxgl.MapMouseEvent) => {
        canvas.style.cursor = "";
        const props = feature.properties as GeoJsonNodeProps;
        const { lat, lng } = event.lngLat;

        const nodeId = props.id;
        const node = craNodeStore.get(nodeId);

        if (node) {
          const prevCoords = craNodeStore.pendingCoordsByNodeId.get(nodeId) ?? [
            node.longitude,
            node.latitude,
          ];
          const distanceDragged = turf.distance(prevCoords, [lng, lat], {
            units: "meters",
          });
          if (distanceDragged > 2) {
            craNodeStore.setNodeCoords(node.id, lat, lng);
          }
        }

        this.map.off("mousemove", onMouseMove);
      });
    };

    this.map.on("mousedown", pointStrokeLayerName, mouseDrag);

    this.locationChangeListenerRemovers.push(() => {
      this.map.off("mouseenter", pointStrokeLayerName, cursorMove);
      this.map.off("mouseleave", pointStrokeLayerName, cursorDefault);
      this.map.off("mousedown", pointStrokeLayerName, mouseDrag);
    });
  }

  configCursorOnHover() {
    const canvas = this.map.getCanvasContainer();

    Object.entries(this.hoverInteractions).forEach(([key, layerId]) => {
      this.map.addInteraction(`enter-${key}`, {
        type: "mouseenter",
        target: { layerId },
        handler: () => {
          canvas.style.cursor = "pointer";
        },
      });

      this.map.addInteraction(`leave-${key}`, {
        type: "mouseleave",
        target: { layerId },
        handler: () => {
          canvas.style.cursor = "";
        },
      });
    });
  }

  disableCursorOnHover() {
    Object.keys(this.hoverInteractions).forEach((key) => {
      this.map.removeInteraction(`enter-${key}`);
      this.map.removeInteraction(`leave-${key}`);
    });
  }

  enableWaySelection() {
    const { craWayStore, craNodeStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const handleSelectRoute = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!;
      const props = feature.properties as GeoJsonNodeProps;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";

        if (craNodeStore.getSelected()?.craWayId !== props.craWayId) {
          craNodeStore.clearSelected();
        }
        craWayStore.select(props.craWayId);
      });
    };
    this.map.on("mousedown", simplifiedStrokeLayerName, handleSelectRoute);

    this.waySelectionListenerRemovers.push(() => {
      this.map.off("mousedown", simplifiedStrokeLayerName, handleSelectRoute);
    });
  }

  disableWaySelection() {
    this.waySelectionListenerRemovers.map((fn) => fn());
    this.waySelectionListenerRemovers = [];
  }

  enableNodeSelection() {
    const { craWayStore, craNodeStore, routeDesignStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const handleSelectNode = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!!;
      const props = feature.properties as GeoJsonNodeProps;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";

        const designingRoute = !!(
          craWayStore.getSelected()?.lifeCycleStage === LifeCycleStage.Design &&
          routeDesignStore.newPlannedTrajectory
        );
        if (!designingRoute) {
          craWayStore.select(props.craWayId);
          craNodeStore.select(props.id);
        }
      });
    };

    this.map.on("mousedown", pointStrokeLayerName, handleSelectNode);
    this.map.on("mousedown", commentLayerName, handleSelectNode);
    this.map.on("mousedown", strokeLayerName, handleSelectNode);

    this.waySelectionListenerRemovers.push(() => {
      this.map.off("mousedown", pointStrokeLayerName, handleSelectNode);
      this.map.off("mousedown", commentLayerName, handleSelectNode);
      this.map.off("mousedown", strokeLayerName, handleSelectNode);
    });
  }

  disableNodeSelection() {
    this.nodeSelectionListenerRemovers.map((fn) => fn());
    this.nodeSelectionListenerRemovers = [];
  }

  extendBounds(bounds: mapboxgl.LngLatBounds) {
    const { craWayStore, craNodeStore, hlaStore } = this.rootStore;

    const selectedCraWay = craWayStore.getSelected();
    const selectedCraNode = craNodeStore.getSelected();
    const selectedHlaWay = hlaStore.getSelected();

    const source = this.map.getSource(sourceName);

    const geoJson = (source as any)._data as GeoJSON.FeatureCollection;

    geoJson.features.forEach((feature) => {
      const props = feature.properties as GeoJsonNodeProps;

      if (
        shouldIncludeInBounds(props, {
          selectedCraNode,
          selectedCraWay,
          selectedHlaWay,
        })
      ) {
        const geometry = feature.geometry as Geometry;
        switch (geometry.type) {
          case "Point": {
            bounds.extend(geometry.coordinates as [number, number]);
            break;
          }
          case "LineString": {
            (geometry.coordinates as [number, number][]).forEach((coords) =>
              bounds.extend(coords),
            );
            break;
          }
          default:
            break;
        }
      }
    });
  }

  private applyMapColoringSettings(node: CraNode): string {
    const { filterStore, mapStore } = this.rootStore;
    const disabledColor = notCodedColor;

    if (mapStore.coloring.type === MapColoringType.Score) {
      if (!node.score) return disabledColor;

      const scoreEntry = node.score[mapStore.coloring.scoreKey];
      return scoreEntry.color;
    }

    if (mapStore.coloring.type === MapColoringType.Treatment) {
      if (!node.treatments?.length) return getBooleanColor(false);
      const coloring = mapStore.coloring as MapColoringTreatment;

      const treatmentFound = node.treatments.some(
        (t) => t.type === coloring.treatmentType,
      );

      return getBooleanColor(treatmentFound);
    }

    if (mapStore.coloring.type === MapColoringType.CraAttribute) {
      const allNodeCodingsByWayId =
        filterStore.originalOrForecastedAllNodeCodingsByWayId;
      const nodeCoding = allNodeCodingsByWayId.get(node.craWayId)?.get(node.id);
      if (!nodeCoding) return disabledColor; // coding not available

      const attribute = CraAttributes.getByName(
        mapStore.coloring.attributeName,
      )!;
      const value = getAttributeValue(attribute.name, nodeCoding.attributes);

      if (attribute.type === CraAttributeType.Select) {
        if (!value) return disabledColor;
        const length = attribute.options.length;
        const optionIndex = attribute.options.findIndex(
          (option) => option.value === value,
        );
        return mapGradientStep(length, optionIndex);
      } else if (attribute.type === CraAttributeType.TextArea) {
        if (!value) return mapGradientEnd;
        return mapGradientStart;
      } else if (attribute.type === CraAttributeType.Number) {
        if (isNaN(value as any)) return disabledColor;
        const limitedValue = Math.max(Math.min(value as number, 90), 0);
        return `#${mapGradient.hsvAt(limitedValue / 90).toHex()}`;
      }
    }

    return disabledColor;
  }
}

/**
 * LOGIC
 */

function buildSimplifiedGeoJson(
  features: GeoJSON.Feature[],
): GeoJSON.Feature[] {
  const linesOnlyGeoJson: FeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };

  if (features.length) {
    const firstItemProps = features[0].properties as GeoJsonNodeProps;

    var previousCraWayId = firstItemProps.craWayId;
    var previousCraWaySelected = firstItemProps.craWaySelected;
    var previousTransparent = firstItemProps.transparent;
    var previousColor = firstItemProps.color;
    var currentSegment: Position[] = [];

    features.forEach((feature, index, list) => {
      const isLastFeature = index === list.length - 1;

      const { geometry } = feature;

      if (geometry.type === "Point") {
        const props = feature.properties as GeoJsonNodeProps;

        if (previousCraWayId === props.craWayId) {
          currentSegment.push(geometry.coordinates);
        }

        if (
          previousColor !== props.color || // new color starts
          previousCraWayId !== props.craWayId || // new way starts
          props.nodeSelected || // makes sure the point is centered with the line
          isLastFeature // makes sure the last segment is included
        ) {
          if (currentSegment.length > 1) {
            linesOnlyGeoJson.features.push({
              type: "Feature",
              properties: {
                color: previousColor,
                craWaySelected: previousCraWaySelected,
                craWayId: previousCraWayId,
                transparent: previousTransparent,
              } as GeoJsonNodeProps,
              geometry: {
                type: "LineString",
                coordinates: currentSegment,
              },
            });
          }

          currentSegment = [geometry.coordinates];
        }

        if (props.nodeSelected) {
          linesOnlyGeoJson.features.push(feature);
        }

        previousColor = props.color;
        previousCraWaySelected = props.craWaySelected;
        previousCraWayId = props.craWayId;
        previousTransparent = props.transparent;
      }
    });
  }

  return turf.simplify(linesOnlyGeoJson, {
    tolerance: simplificationTolerance,
    highQuality: false,
    mutate: true,
  }).features;
}

interface ExtendBoundsOptions {
  selectedCraNode?: CraNode;
  selectedCraWay?: CraWay;
  selectedHlaWay?: HlaWay;
}
const shouldIncludeInBounds = (
  props: GeoJsonNodeProps,
  { selectedCraNode, selectedCraWay, selectedHlaWay }: ExtendBoundsOptions,
) => {
  if (selectedHlaWay) return false;
  if (selectedCraNode) return props.id === selectedCraNode.id;
  if (selectedCraWay) return props.craWayId === selectedCraWay.id;
  return true;
};

const zoomThreshhold = 16;
const simplificationTolerance = 0.00004;
const weakOpacity = 0.5;

const isLineString = ["==", "$type", "LineString"];
const isPoint = ["==", "$type", "Point"];
const isNodeSelected = ["boolean", ["get", "nodeSelected"], false];
const isMultiNodeSelected = ["boolean", ["get", "nodeMultiSelected"], false];
const isPhotoMissing = ["boolean", ["get", "photoMissing"], false];
const isCraWaySelected = ["boolean", ["get", "craWaySelected"], false];
const isTransparent = ["boolean", ["get", "transparent"], false];

/**
 * TEMPLATES
 */

const lineLayerConfigTemplate: mapboxgl.LineLayerSpecification = {
  id: "",
  type: "line",
  source: "",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "color"],
    "line-width": 7,
  },
  filter: isLineString,
};

const lineStrokeLayerConfigTemplate: mapboxgl.LineLayerSpecification = {
  id: "",
  source: "",
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-opacity": ["case", isTransparent, weakOpacity, 1],
    "line-color": defaultStrokeColor,
    "line-width": ["case", isCraWaySelected, 11, 9],
  },
  filter: isLineString,
};

const directionLayerConfigTemplate: mapboxgl.SymbolLayerSpecification = {
  id: "",
  source: "",
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 50,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": defaultStrokeColor,
  },
};

/**
 * MAIN SOURCE
 */

const sourceName = CustomSource.Cra;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

// The node lines
const lineLayerName = `${sourceName}-line`;
const lineLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineLayerConfigTemplate,
  id: lineLayerName,
  source: sourceName,
  minzoom: zoomThreshhold,
};

// The stroke around the node lines
const strokeLayerName = `${sourceName}-stroke`;
const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineStrokeLayerConfigTemplate,
  source: sourceName,
  id: strokeLayerName,
  minzoom: zoomThreshhold,
};

// The center of the node dots
const pointLayerName = `${sourceName}-point`;
const pointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": ["case", isNodeSelected, 9, 4],
    "circle-color": ["case", isPhotoMissing, "#ff0000", ["get", "color"]],
  },
  minzoom: zoomThreshhold,
  filter: isPoint,
};

// The stroke around the node dots
const pointStrokeLayerName = `${sourceName}-point-stroke`;
const pointStrokeLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointStrokeLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-opacity": ["case", isTransparent, weakOpacity, 1],
    "circle-radius": ["case", isNodeSelected, 11, 8],
    "circle-color": defaultStrokeColor,
  },
  minzoom: zoomThreshhold,
  filter: isPoint,
};

// Invisible dots that enable us to select nodes from the map even if we don't want to show them to the user
const pointInvisibleLayerName = `${sourceName}-invisible-point`;
const pointInvisibleLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointInvisibleLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": 12,
    "circle-color": "transparent",
  },
  filter: isPoint,
};

// The blue dots that appear on high zoom when you select multiple nodes
const pointMultiSelectedLayerName = `${sourceName}-multi-selected-point`;
const pointMultiSelectedLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointMultiSelectedLayerName,
  type: "circle",
  source: sourceName,
  paint: {
    "circle-radius": [
      "case",
      isMultiNodeSelected,
      ["case", isNodeSelected, 15, 11],
      0,
    ],
    "circle-color": "#00b7db",
  },
  minzoom: zoomThreshhold,
  filter: isPoint,
};

// The blue line that appears on low zoom when you select multiple nodes
const lineMultiSelectedLayerName = `${sourceName}-multi-selected-line`;
const lineMultiSelectedLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineStrokeLayerConfigTemplate,
  id: lineMultiSelectedLayerName,
  source: sourceName,
  paint: {
    "line-width": ["case", isMultiNodeSelected, 3, 0],
    "line-color": "#00b7db",
  },
  maxzoom: zoomThreshhold,
};

// The comment icon that appears on nodes with comments
const commentLayerName = `${sourceName}-comment`;
const commentLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: commentLayerName,
  type: "symbol",
  source: sourceName,
  layout: {
    "icon-image": "comment-icon",
    "icon-size": 1.0,
    "icon-offset": [24, 0],
  },
  filter: ["all", isPoint, ["==", "showCommentIcon", true]],
};

// Adds arrows to the way lines so that you know the direction
const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  ...directionLayerConfigTemplate,
  id: directionLayerName,
  source: sourceName,
  minzoom: zoomThreshhold,
};

/**
 * SIMPLIFIED SOURCE
 */

const simplifiedSourceName = CustomSource.SimplifiedCra;
const simplifiedSourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const simplifiedStrokeLayerName = `${simplifiedSourceName}-stroke`;
const simplifiedStrokeLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineStrokeLayerConfigTemplate,
  source: simplifiedSourceName,
  id: simplifiedStrokeLayerName,
  maxzoom: zoomThreshhold,
  minzoom: 12,
};

const simplifiedLineLayerName = `${simplifiedSourceName}-line`;
const simplifiedLineLayerConfig: mapboxgl.LineLayerSpecification = {
  ...lineLayerConfigTemplate,
  id: simplifiedLineLayerName,
  source: simplifiedSourceName,
  maxzoom: zoomThreshhold,
};

const simplifiedPointLayerName = `${simplifiedSourceName}-point`;
const simplifiedPointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: simplifiedPointLayerName,
  type: "circle",
  source: simplifiedSourceName,
  paint: {
    "circle-radius": 11,
    "circle-color": defaultStrokeColor,
  },
  maxzoom: zoomThreshhold,
  filter: isPoint,
};

const simplifiedDirectionLayerName = `${simplifiedSourceName}-direction`;
const simplifiedDirectionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  ...directionLayerConfigTemplate,
  id: simplifiedDirectionLayerName,
  source: simplifiedSourceName,
  maxzoom: zoomThreshhold,
};

export const craNodeShiftDragLayerName = pointInvisibleLayerName;
